import React from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSubscription } from '../../hooks/useSubscription';
import { getStoredAuth } from '../../services/nativeAppleSignIn';

export default function UpgradePrompt({ reason, onDismiss }) {
  const navigate = useNavigate();
  const { subscription, startTrial } = useSubscription();

  const hasUsedTrial = subscription.trialStartDate !== null;
  const isSignedIn = !!getStoredAuth();

  const handleStartTrial = async () => {
    try {
      await startTrial();
      toast.success('7-day free trial started');
      onDismiss?.();
    } catch (err) {
      if (err.code === 'auth_required') {
        toast.error('Sign in to start your free trial');
        onDismiss?.();
        navigate('/profile');
        return;
      }
      toast.error(err.message || 'Could not start trial');
    }
  };

  const handleSignIn = () => {
    onDismiss?.();
    navigate('/profile');
  };

  const handleViewPlans = () => {
    onDismiss?.();
    navigate('/pricing');
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onDismiss}
    >
      <div
        className="relative w-full max-w-md glass-card p-6 space-y-4 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-full text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-bg-elevated)] transition-colors"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="flex justify-center pt-2">
          <svg viewBox="0 0 48 48" width="56" height="56">
            <circle cx="24" cy="24" r="22" fill="rgba(239,68,68,0.15)" stroke="rgba(239,68,68,0.4)" strokeWidth="1.5" />
            <ellipse cx="24" cy="26" rx="8" ry="7.5" fill="#ef4444" opacity="0.9" />
            <path d="M24 19 Q21 14 18 12" stroke="#4ade80" strokeWidth="2" fill="none" strokeLinecap="round" />
            <path d="M24 19 Q27 14 30 12" stroke="#4ade80" strokeWidth="2" fill="none" strokeLinecap="round" />
            <ellipse cx="21" cy="24" rx="2" ry="1.5" fill="white" opacity="0.15" />
          </svg>
        </div>

        <h3 className="text-xl font-bold text-[color:var(--color-text-primary)]">Unlock Premium</h3>

        {reason && (
          <p className="text-sm text-[color:var(--color-text-secondary)] leading-relaxed">{reason}</p>
        )}

        <ul className="text-sm text-[color:var(--color-text-secondary)] space-y-1 list-none">
          <li>✓ All 8 plant varieties</li>
          <li>✓ Calendar view & detailed analytics</li>
          <li>✓ Ambient focus sounds</li>
          <li>✓ Harvest Milestones</li>
        </ul>

        {!hasUsedTrial && isSignedIn ? (
          <button
            type="button"
            onClick={handleStartTrial}
            className="w-full px-4 py-3 rounded-xl bg-tomato-500 hover:bg-tomato-600 active:bg-tomato-700 text-white font-semibold transition-colors shadow-glow-sm"
          >
            Start 7-Day Free Trial
          </button>
        ) : !hasUsedTrial && !isSignedIn ? (
          <button
            type="button"
            onClick={handleSignIn}
            className="w-full px-4 py-3 rounded-xl bg-tomato-500 hover:bg-tomato-600 active:bg-tomato-700 text-white font-semibold transition-colors shadow-glow-sm"
          >
            Sign in to start 7-day free trial
          </button>
        ) : (
          <button
            type="button"
            onClick={handleViewPlans}
            className="w-full px-4 py-3 rounded-xl bg-tomato-500 hover:bg-tomato-600 active:bg-tomato-700 text-white font-semibold transition-colors shadow-glow-sm"
          >
            Upgrade to Premium
          </button>
        )}

        <button
          type="button"
          onClick={handleViewPlans}
          className="w-full px-4 py-2 text-sm font-medium text-[color:var(--color-text-secondary)] hover:text-[color:var(--color-text-primary)] transition-colors"
        >
          See Premium
        </button>
      </div>
    </div>
  );
}
